import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "status", "save", "discard", "finalize", "gold", "silver", "bronze", "removed"]
  static values = {
    hint: String,
    unsaved: String,
    leave: String,
    blocked: String,
    contestId: Number
  }

  connect() {
    this.allowLeave = false
    this.onSubmit = (event) => {
      if (!this.dirty()) {
        event.preventDefault()
        return
      }
      sessionStorage.removeItem(this.storageKey)
      this.allowLeave = true
    }
    this.onBeforeVisit = (event) => {
      if (this.allowLeave || !this.dirty() || !this.shouldConfirmLeave(event.detail.url)) return
      if (!window.confirm(this.leaveValue)) {
        event.preventDefault()
        return
      }
      sessionStorage.removeItem(this.storageKey)
      this.allowLeave = true
    }
    this.onBeforeUnload = (event) => {
      if (this.allowLeave || !this.dirty()) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.element.addEventListener("submit", this.onSubmit)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    window.addEventListener("beforeunload", this.onBeforeUnload)
    this.restoreDraft()
    this.render()
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    window.removeEventListener("beforeunload", this.onBeforeUnload)
  }

  assign(event) {
    event.preventDefault()
    const position = event.params.position
    const card = event.target.closest("[data-winner-draft-target='card']")
    if (!card || card.dataset.removed === "true") return

    this.cardTargets.forEach((item) => {
      if (item !== card && item.dataset.position === position) item.dataset.position = ""
    })
    card.dataset.position = position
    this.render()
  }

  clear(event) {
    event.preventDefault()
    const card = event.target.closest("[data-winner-draft-target='card']")
    if (!card) return
    card.dataset.position = ""
    this.render()
  }

  remove(event) {
    event.preventDefault()
    const card = event.target.closest("[data-winner-draft-target='card']")
    if (!card || card.dataset.removed === "true") return
    card.dataset.heldPosition = card.dataset.position || ""
    card.dataset.position = ""
    card.dataset.removed = "true"
    this.render()
  }

  restore(event) {
    event.preventDefault()
    const card = event.target.closest("[data-winner-draft-target='card']")
    if (!card) return
    card.dataset.removed = "false"
    card.dataset.position = card.dataset.heldPosition || ""
    const position = card.dataset.position
    if (position) {
      this.cardTargets.forEach((item) => {
        if (item !== card && item.dataset.position === position) item.dataset.position = ""
      })
    }
    this.render()
  }

  discard(event) {
    event.preventDefault()
    this.cardTargets.forEach((card) => {
      card.dataset.position = card.dataset.initialPosition || ""
      card.dataset.removed = "false"
      card.dataset.heldPosition = ""
    })
    sessionStorage.removeItem(this.storageKey)
    this.render()
  }

  dirty() {
    return this.cardTargets.some((card) => {
      const position = card.dataset.removed === "true" ? "" : (card.dataset.position || "")
      return card.dataset.removed === "true" || position !== (card.dataset.initialPosition || "")
    })
  }

  render() {
    const assigned = { gold: "", silver: "", bronze: "" }

    this.cardTargets.forEach((card) => {
      const removed = card.dataset.removed === "true"
      const position = removed ? "" : (card.dataset.position || "")
      if (position) assigned[position] = card.dataset.photoId

      card.querySelectorAll("[data-medal]").forEach((button) => {
        button.dataset.active = button.dataset.medal === position ? "true" : "false"
        button.disabled = removed
      })

      const assignment = card.querySelector("[data-assignment]")
      const badge = card.querySelector("[data-badge]")
      if (assignment) assignment.hidden = !position
      if (badge && position) {
        const source = card.querySelector(`[data-medal="${position}"]`)
        badge.textContent = source ? source.textContent.trim() : ""
      }

      const remove = card.querySelector("[data-remove]")
      const banner = card.querySelector("[data-restore-banner]")
      if (remove) remove.hidden = removed
      if (banner) banner.hidden = !removed
    })

    this.goldTarget.value = assigned.gold
    this.silverTarget.value = assigned.silver
    this.bronzeTarget.value = assigned.bronze
    this.removedTarget.replaceChildren()
    this.cardTargets.filter((card) => card.dataset.removed === "true").forEach((card) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "removed_photo_ids[]"
      input.value = card.dataset.photoId
      this.removedTarget.appendChild(input)
    })

    const dirty = this.dirty()
    this.saveTarget.disabled = !dirty
    this.discardTarget.disabled = !dirty
    if (this.hasStatusTarget) {
      this.statusTarget.dataset.dirty = dirty ? "true" : "false"
      this.statusTarget.textContent = dirty ? this.unsavedValue : this.hintValue
    }
    if (this.hasFinalizeTarget) {
      this.finalizeTarget.disabled = dirty
      this.finalizeTarget.title = dirty ? this.blockedValue : ""
    }
    this.persist()
  }

  get storageKey() {
    return `winner-draft:${this.contestIdValue}`
  }

  restoreDraft() {
    const raw = sessionStorage.getItem(this.storageKey)
    if (!raw) return

    let cards
    try {
      cards = JSON.parse(raw)
    } catch {
      sessionStorage.removeItem(this.storageKey)
      return
    }

    this.cardTargets.forEach((card) => {
      const saved = cards[card.dataset.photoId]
      if (!saved) return
      card.dataset.position = saved.position || ""
      card.dataset.removed = saved.removed ? "true" : "false"
      card.dataset.heldPosition = saved.held || ""
    })
  }

  persist() {
    if (!this.dirty()) {
      sessionStorage.removeItem(this.storageKey)
      return
    }

    const cards = {}
    this.cardTargets.forEach((card) => {
      cards[card.dataset.photoId] = {
        position: card.dataset.position || "",
        removed: card.dataset.removed === "true",
        held: card.dataset.heldPosition || ""
      }
    })
    sessionStorage.setItem(this.storageKey, JSON.stringify(cards))
  }

  shouldConfirmLeave(url) {
    const next = new URL(url, window.location.origin)
    const here = new URL(window.location.href)
    if (next.pathname === here.pathname && next.search === here.search) return false
    if (/^\/photos\/\d+/.test(next.pathname)) return false
    return true
  }
}
